import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Card, Modal, Tooltip } from '@components';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

interface TopProductsProps {
  data: any; // Structure complète avec summary, aiAnalysis, etc.
}

export const TopProducts: React.FC<TopProductsProps> = ({ data }) => {
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  // Gestion sécurisée des données
  const safeData = data?.data || [];
  const summary = data?.summary;
  const aiAnalysis = data?.aiAnalysis;

  const sortedProducts = useMemo(
    () => [...safeData].sort((a, b) => b.quantitySold - a.quantitySold),
    [safeData]
  );

  const chartData = useMemo(() => {
    const labels = sortedProducts.map((product) => product.name);
    const quantities = sortedProducts.map((product) => product.quantitySold);
    const revenues = sortedProducts.map((product) => product.totalRevenue);

    return {
      labels,
      datasets: [
        {
          label: 'Revenus générés',
          data: revenues,
          backgroundColor: 'rgba(139, 92, 246, 0.6)',
          borderColor: 'rgba(139, 92, 246, 1)',
          borderWidth: 2,
          yAxisID: 'y-revenue',
        },
        {
          label: 'Quantités vendues',
          data: quantities,
          backgroundColor: 'rgba(16, 185, 129, 0.6)',
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 2,
          yAxisID: 'y-quantity',
        },
      ],
    };
  }, [sortedProducts]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          callbacks: {
            label: function (context: any) {
              const value = context.raw;
              return `${context.dataset.label}: ${value.toLocaleString()}${
                context.dataset.label === 'Revenus générés' ? ' FCFA' : ''
              }`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { size: 11 },
            maxRotation: 45,
          },
        },
        'y-revenue': {
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: 'Revenus (FCFA)',
            font: { size: 12 },
          },
          ticks: {
            font: { size: 11 },
            callback: function (value: number) {
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M';
              }
              if (value >= 1000) {
                return (value / 1000).toFixed(0) + 'K';
              }
              return value.toLocaleString();
            },
          },
        },
        'y-quantity': {
          type: 'linear',
          position: 'right',
          title: {
            display: true,
            text: 'Quantités vendues',
            font: { size: 12 },
          },
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            font: { size: 11 },
            callback: function (value: number) {
              return value.toLocaleString();
            },
          },
        },
      },
    }),
    []
  );

  // Fonction pour formater les grands nombres
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="space-y-6">
      {/* Métriques de résumé */}
      {summary && (
        <Card customClassName="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Tooltip content="Le produit générant le plus de revenus de votre catalogue">
              <div className="text-center p-4 bg-gradient-to-br from-violet-50 to-purple-100 dark:from-violet-900/30 dark:to-purple-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-violet-200 dark:border-violet-700">
                <div className="text-2xl mb-2">👑</div>
                <h4 className="font-semibold text-violet-800 dark:text-slate-100 mb-2">Produit Star</h4>
                <p className="text-lg font-bold text-violet-900 dark:text-gray-50 mb-1">
                  {summary.topProduct.name}
                </p>
                <p className="text-sm font-medium text-violet-700 dark:text-stone-200">
                  {formatNumber(summary.topProduct.revenue)} FCFA
                </p>
              </div>
            </Tooltip>

            <Tooltip content="Nombre total de produits différents vendus sur la période">
              <div className="text-center p-4 bg-gradient-to-br from-cyan-50 to-blue-100 dark:from-cyan-900/30 dark:to-blue-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-cyan-200 dark:border-cyan-700">
                <div className="text-2xl mb-2">📦</div>
                <h4 className="font-semibold text-cyan-800 dark:text-zinc-100 mb-2">Produits Actifs</h4>
                <p className="text-xl font-bold text-cyan-900 dark:text-neutral-50">
                  {summary.totalProducts}
                </p>
              </div>
            </Tooltip>

            <Tooltip content="Revenus total généré par tous les produits du top 10">
              <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/30 dark:to-teal-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-emerald-200 dark:border-emerald-700">
                <div className="text-2xl mb-2">💰</div>
                <h4 className="font-semibold text-emerald-800 dark:text-white mb-2">CA Total</h4>
                <p className="text-xl font-bold text-emerald-900 dark:text-slate-50">
                  {formatNumber(summary.totalRevenue)} FCFA
                </p>
              </div>
            </Tooltip>

            <Tooltip content="Revenus moyen par produit dans le top 10">
              <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-amber-100 dark:from-orange-900/30 dark:to-amber-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-orange-200 dark:border-orange-700">
                <div className="text-2xl mb-2">📊</div>
                <h4 className="font-semibold text-orange-800 dark:text-stone-100 mb-2">CA Moyen</h4>
                <p className="text-xl font-bold text-orange-900 dark:text-gray-100">
                  {formatNumber(summary.averageRevenuePerProduct)} FCFA
                </p>
              </div>
            </Tooltip>
          </div>
        </Card>
      )}

      {/* Graphique principal */}
      <Card customClassName="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Top 10 des Produits les Plus Vendus
          </h3>
          {aiAnalysis && (
            <button
              onClick={() => setShowAnalysisModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <span className="text-lg">🤖</span>
              Analyse IA
            </button>
          )}
        </div>

        {sortedProducts.length === 0 ? (
          <p className="text-center text-gray-500 italic py-8">Aucune donnée à afficher.</p>
        ) : (
          <div className="relative h-64 md:h-80">
            <Bar data={chartData} options={options as any} />
          </div>
        )}
      </Card>

      {/* Modal d'analyse IA */}
      <Modal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        title="🤖 Analyse IA des Produits"
        customClassName="max-w-4xl"
      >
        {aiAnalysis && (
          <div className="space-y-6">
            {/* Badge de confiance */}
            <div className="flex justify-center">
              <span className="px-4 py-2 text-sm bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-full border border-purple-200">
                <span className="font-semibold">Niveau de confiance:</span> {aiAnalysis.confidence || 0}%
              </span>
            </div>

            {/* Métriques IA avec tooltips */}
            {aiAnalysis.metrics && (
              <div>
                <h4 className="text-lg font-bold text-blue-400 mb-4">
                  📊 Métriques de Performance Produits
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {aiAnalysis.metrics.concentrationRatio !== undefined && (
                    <Tooltip 
                      content="Pourcentage du chiffre d'affaires généré par les 3 meilleurs produits"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-rose-50 to-pink-100 dark:from-rose-900/30 dark:to-pink-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-rose-300 dark:border-rose-600">
                        <div className="text-2xl mb-2">🎯</div>
                        <h5 className="font-medium text-sm text-rose-700 dark:text-rose-200 mb-1">Concentration Top 3</h5>
                        <p className="text-xl font-bold text-rose-800 dark:text-rose-100">
                          {aiAnalysis.metrics.concentrationRatio?.toFixed(1) || 'N/A'}%
                        </p>
                      </div>
                    </Tooltip>
                  )}
                  
                  {aiAnalysis.metrics.diversificationScore !== undefined && (
                    <Tooltip 
                      content="Score de diversification du catalogue sur 100. Plus c'est élevé, plus votre offre est diversifiée"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-green-300 dark:border-green-600">
                        <div className="text-2xl mb-2">🌈</div>
                        <h5 className="font-medium text-sm text-green-700 dark:text-green-200 mb-1">Score Diversification</h5>
                        <p className="text-xl font-bold text-green-800 dark:text-green-100">
                          {aiAnalysis.metrics.diversificationScore?.toFixed(1) || 'N/A'}/100
                        </p>
                      </div>
                    </Tooltip>
                  )}
                  
                  {aiAnalysis.metrics.averageTicketPerProduct !== undefined && (
                    <Tooltip 
                      content="Prix moyen de vente par unité calculé sur tous les produits du top 10"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-blue-300 dark:border-blue-600">
                        <div className="text-2xl mb-2">🎫</div>
                        <h5 className="font-medium text-sm text-blue-700 dark:text-blue-200 mb-1">Ticket Moyen</h5>
                        <p className="text-xl font-bold text-blue-800 dark:text-blue-100">
                          {formatNumber(aiAnalysis.metrics.averageTicketPerProduct || 0)} FCFA
                        </p>
                      </div>
                    </Tooltip>
                  )}
                  
                  {aiAnalysis.metrics.topPerformersCount !== undefined && (
                    <Tooltip 
                      content="Nombre de produits considérés comme des top performers dans votre catalogue"
                      position="bottom"
                    >
                      <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-800/40 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-help border border-amber-300 dark:border-amber-600">
                        <div className="text-2xl mb-2">⭐</div>
                        <h5 className="font-medium text-sm text-amber-700 dark:text-amber-200 mb-1">Top Performers</h5>
                        <p className="text-xl font-bold text-amber-800 dark:text-amber-100">
                          {aiAnalysis.metrics.topPerformersCount}/{summary?.totalProducts || 10}
                        </p>
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}

            {/* Insights détaillés de l'IA */}
            {aiAnalysis.insights && Object.keys(aiAnalysis.insights).length > 0 && (
              <div>
                <h4 className="text-lg font-bold text-green-400 mb-4">
                  🧠 Analyse Avancée des Produits
                </h4>
                <div className="space-y-4">
                  {Object.entries(aiAnalysis.insights).map(([insightType, insightContent]: [string, any]) => {
                    // Vérifier si c'est un JSON string
                    let content = insightContent;
                    if (typeof insightContent === 'string' && insightContent.includes('```json')) {
                      try {
                        const jsonMatch = insightContent.match(/```json\n([\s\S]*?)\n```/);
                        if (jsonMatch) {
                          const jsonData = JSON.parse(jsonMatch[1]);
                          content = jsonData;
                        }
                      } catch (e) {
                        content = insightContent;
                      }
                    }

                    const getInsightIcon = (type: string) => {
                      const icons: { [key: string]: string } = {
                        productPerformance: '📈',
                        marketTrends: '📊',
                        stockRecommendations: '📦',
                        pricingStrategy: '💰'
                      };
                      return icons[type] || '💡';
                    };

                    const getInsightTitle = (type: string) => {
                      const titles: { [key: string]: string } = {
                        productPerformance: 'Performance Produits',
                        marketTrends: 'Tendances Marché',
                        stockRecommendations: 'Recommandations Stock',
                        pricingStrategy: 'Stratégie Prix'
                      };
                      return titles[type] || type;
                    };

                    return (
                      <div key={insightType} className="p-5 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-800 dark:to-gray-800 rounded-xl shadow-sm border border-slate-300 dark:border-slate-600">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-2xl">{getInsightIcon(insightType)}</span>
                          <h5 className="font-semibold text-lg text-slate-800 dark:text-slate-100">
                            {getInsightTitle(insightType)}
                          </h5>
                        </div>
                        
                        {typeof content === 'object' && content.insights ? (
                          <div className="space-y-4">
                            {Object.entries(content.insights).map(([key, value]: [string, any]) => (
                              <div key={key} className="pl-4 border-l-3 border-indigo-400 dark:border-indigo-500 bg-white dark:bg-slate-700 rounded-r-lg p-3">
                                <h6 className="font-semibold text-sm text-indigo-700 dark:text-indigo-300 mb-2 capitalize">
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </h6>
                                <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                                  {typeof value === 'string' ? value : JSON.stringify(value)}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="pl-4 border-l-3 border-indigo-400 dark:border-indigo-500 bg-white dark:bg-slate-700 rounded-r-lg p-3">
                            <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                              {typeof content === 'string' ? content : JSON.stringify(content)}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recommandations stratégiques */}
            {aiAnalysis.recommendations && Object.keys(aiAnalysis.recommendations).length > 0 && (
              <div>
                <h4 className="text-lg font-bold text-orange-400 mb-4">
                  💡 Recommandations Produits
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(aiAnalysis.recommendations).map(([category, recommendations]: [string, any]) => (
                    recommendations && recommendations.length > 0 && (
                      <div key={category} className="p-5 bg-gradient-to-br from-white to-slate-50 dark:from-slate-700 dark:to-slate-800 rounded-xl shadow-sm border border-slate-300 dark:border-slate-600">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-xl">
                            {category === 'inventory' ? '📦' : 
                             category === 'marketing' ? '📈' :
                             category === 'pricing' ? '💰' :
                             category === 'product' ? '🛍️' :
                             '💼'}
                          </span>
                          <h5 className="font-semibold text-slate-800 dark:text-slate-100 capitalize">
                            {category === 'inventory' ? 'Inventaire' : 
                             category === 'marketing' ? 'Marketing' :
                             category === 'pricing' ? 'Prix' :
                             category === 'product' ? 'Produits' :
                             category}
                          </h5>
                        </div>
                        <ul className="space-y-3">
                          {recommendations.slice(0, 4).map((rec: string, index: number) => (
                            <li key={index} className="flex items-start gap-3 p-2 bg-slate-100 dark:bg-slate-600 rounded-lg">
                              <span className="text-indigo-600 dark:text-indigo-400 mt-1 text-sm font-bold">•</span>
                              <span className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Informations sur l'analyse */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Analyse générée automatiquement par IA
                </span>
                <span>
                  {aiAnalysis.generatedAt ? new Date(aiAnalysis.generatedAt).toLocaleDateString('fr-FR') : ''} à {aiAnalysis.generatedAt ? new Date(aiAnalysis.generatedAt).toLocaleTimeString('fr-FR') : ''}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
